import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getLoginUrl } from "@/const";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

type MarketingLayoutProps = {
  children: React.ReactNode;
};

const navItems = [
  { label: "Home", path: "/" },
  { label: "Docs", path: "/docs" },
  { label: "Blog", path: "/blog" },
  { label: "Skills", path: "/skills" },
  { label: "Pricing", path: "/pricing" },
];

export default function MarketingLayout({ children }: MarketingLayoutProps) {
  const [location, setLocation] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background grid grid-rows-[auto_1fr_auto] max-w-[100vw] overflow-x-hidden">
      <header className="border-b border-border bg-card">
        <div className="container py-4 flex items-center justify-between gap-6">
          <button
            className="flex items-center gap-3"
            onClick={() => setLocation("/")}
            type="button"
            aria-label="Go to home page"
          >
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl font-mono">
                OO
              </span>
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-foreground leading-tight">
                Double O
              </p>
              <p className="text-xs text-muted-foreground">Secrets Store</p>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-2">
            {navItems.map(item => {
              const isActive = location === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setLocation(item.path)}
                >
                  {item.label}
                </Button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Button asChild>
              <a href={getLoginUrl()}>Sign In</a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-11 w-11"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

        </div>
      </header>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-72">
          <SheetHeader>
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col gap-2 px-4">
            {navItems.map(item => {
              const isActive = location === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? "secondary" : "ghost"}
                  className="justify-start"
                  onClick={() => {
                    setLocation(item.path);
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
            <Button asChild className="mt-4">
              <a href={getLoginUrl()}>Sign In</a>
            </Button>
          </nav>
        </SheetContent>
      </Sheet>

      <main className="min-w-0">
        {children}
      </main>

      <footer className="border-t border-border bg-card">
        <div className="container py-10">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {/* Brand column */}
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold font-mono">
                    OO
                  </span>
                </div>
                <span className="font-semibold text-card-foreground">Double O</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Secure secrets management for vibe-coding and AI agents. Stop hardcoding API keys.
              </p>
            </div>

            {/* Product column */}
            <nav aria-label="Product links">
              <h4 className="text-sm font-semibold text-card-foreground mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/docs" className="text-muted-foreground hover:text-primary transition-colors">Docs</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="/blog" className="text-muted-foreground hover:text-primary transition-colors">Blog</a></li>
                <li><a href="/skills" className="text-muted-foreground hover:text-primary transition-colors">Skills</a></li>
              </ul>
            </nav>

            {/* Resources column */}
            <nav aria-label="Resources">
              <h4 className="text-sm font-semibold text-card-foreground mb-3">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://pypi.org/project/double-o/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    PyPI Package
                  </a>
                </li>
                <li><a href="https://x.com/FlorisFok" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">X (Twitter)</a></li>
                <li><a href="https://www.instagram.com/trydoubleo" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">Instagram</a></li>
              </ul>
            </nav>

            {/* Legal column */}
            <nav aria-label="Legal">
              <h4 className="text-sm font-semibold text-card-foreground mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms of Service</a></li>
              </ul>
            </nav>
          </div>

          <div className="border-t border-border mt-8 pt-4 text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Double O. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
